import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { graphqlRequest } from './graphql'
import type {
  Coordinates as SharedCoordinates,
  AddressSuggestion as SharedAddressSuggestion,
  Address as SharedAddress,
  DeliveryZone as SharedDeliveryZone,
  DeliveryEstimate as SharedDeliveryEstimate,
  Route as SharedRoute,
  RouteStep as SharedRouteStep,
  SaveAddressInput as SharedSaveAddressInput,
  UpdateAddressInput as SharedUpdateAddressInput,
} from '@shinkansen/graphql'

/**
 * Coordinates interface - using shared type from GraphQL schema
 */
export type Coordinates = SharedCoordinates

/**
 * Address suggestion interface - using shared type from GraphQL schema
 */
export type AddressSuggestion = SharedAddressSuggestion

/**
 * Address interface - using shared type from GraphQL schema
 */
export type Address = SharedAddress

/**
 * Delivery zone interface - using shared type from GraphQL schema
 */
export type DeliveryZone = SharedDeliveryZone

/**
 * Delivery estimate interface - using shared type from GraphQL schema
 */
export type DeliveryEstimate = SharedDeliveryEstimate

/**
 * Route step interface - using shared type from GraphQL schema
 */
export type RouteStep = SharedRouteStep

/**
 * Route interface - using shared type from GraphQL schema
 */
export type Route = SharedRoute

/**
 * Save address input - using shared type from GraphQL schema
 */
export type SaveAddressInput = SharedSaveAddressInput

/**
 * Update address input - using shared type from GraphQL schema
 */
export type UpdateAddressInput = SharedUpdateAddressInput

export function useSearchAddresses(query: string, enabled = true) {
  return useQuery({
    queryKey: ['searchAddresses', query],
    queryFn: async () => {
      const result = await graphqlRequest<{ searchAddresses: AddressSuggestion[] }>(`
        query SearchAddresses($query: String!, $limit: Int) {
          searchAddresses(query: $query, limit: $limit) {
            displayName
            street
            district
            city
            coordinates { lat lng }
          }
        }
      `, { query, limit: 5 })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.searchAddresses ?? []
    },
    enabled: enabled && query.length >= 3,
    staleTime: 60000,
  })
}

export function useReverseGeocode(
  lat: number,
  lng: number,
  enabled = true,
  onResolved?: (suggestion: AddressSuggestion) => void
) {
  const query = useQuery({
    queryKey: ['reverseGeocode', lat, lng],
    queryFn: async () => {
      const result = await graphqlRequest<{ reverseGeocode: AddressSuggestion }>(`
        query ReverseGeocode($lat: Float!, $lng: Float!) {
          reverseGeocode(lat: $lat, lng: $lng) {
            displayName
            street
            district
            city
            coordinates { lat lng }
          }
        }
      `, { lat, lng })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.reverseGeocode
    },
    enabled,
  })

  useEffect(() => {
    if (query.data) {
      onResolved?.(query.data)
    }
  }, [query.data, onResolved])

  return query
}

export function useDeliveryEstimate(lat: number, lng: number, enabled = true) {
  return useQuery({
    queryKey: ['deliveryEstimate', lat, lng],
    queryFn: async () => {
      const result = await graphqlRequest<{ getDeliveryEstimate: DeliveryEstimate }>(`
        query GetDeliveryEstimate($lat: Float!, $lng: Float!) {
          getDeliveryEstimate(lat: $lat, lng: $lng) {
            isDeliverable
            zone { id name baseFee minOrderAmount }
            fee
            estimatedMinutes
          }
        }
      `, { lat, lng })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.getDeliveryEstimate
    },
    enabled,
  })
}

export function useRoute(fromLat: number, fromLng: number, toLat: number, toLng: number, enabled = true) {
  return useQuery({
    queryKey: ['route', fromLat, fromLng, toLat, toLng],
    queryFn: async () => {
      const result = await graphqlRequest<{ getRoute: Route | null }>(`
        query GetRoute($fromLat: Float!, $fromLng: Float!, $toLat: Float!, $toLng: Float!) {
          getRoute(fromLat: $fromLat, fromLng: $fromLng, toLat: $toLat, toLng: $toLng) {
            distanceMeters
            durationSeconds
            geometry
            steps {
              instruction
              distanceMeters
              durationSeconds
              coordinates { lat lng }
            }
          }
        }
      `, { fromLat, fromLng, toLat, toLng })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.getRoute
    },
    enabled,
  })
}

export function useMyAddresses() {
  return useQuery({
    queryKey: ['myAddresses'],
    queryFn: async () => {
      const result = await graphqlRequest<{ myAddresses: Address[] }>(`
        query MyAddresses {
          myAddresses {
            id
            label
            fullAddress
            street
            district
            city
            province
            postalCode
            country
            coordinates { lat lng }
            notes
            isDefault
            createdAt
          }
        }
      `)
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.myAddresses ?? []
    },
  })
}

export function useSaveAddress() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (input: SaveAddressInput) => {
      const result = await graphqlRequest<{ saveAddress: Address }>(`
        mutation SaveAddress($input: SaveAddressInput!) {
          saveAddress(input: $input) {
            id
            label
            fullAddress
            coordinates { lat lng }
            isDefault
          }
        }
      `, { input })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.saveAddress
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myAddresses'] })
    },
  })
}

export function useUpdateAddress() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateAddressInput }) => {
      const result = await graphqlRequest<{ updateAddress: Address }>(`
        mutation UpdateAddress($id: UUID!, $input: UpdateAddressInput!) {
          updateAddress(id: $id, input: $input) {
            id
            label
            fullAddress
            isDefault
          }
        }
      `, { id, input })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.updateAddress
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myAddresses'] })
    },
  })
}

export function useDeleteAddress() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await graphqlRequest<{ deleteAddress: boolean }>(`
        mutation DeleteAddress($id: UUID!) {
          deleteAddress(id: $id)
        }
      `, { id })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deleteAddress
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myAddresses'] })
    },
  })
}

export function useSetDefaultAddress() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await graphqlRequest<{ setDefaultAddress: Address }>(`
        mutation SetDefaultAddress($id: UUID!) {
          setDefaultAddress(id: $id) {
            id
            isDefault
          }
        }
      `, { id })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.setDefaultAddress
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myAddresses'] })
    },
  })
}
